import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, PaperPlaneTilt } from "@phosphor-icons/react";

export default function BulkEmailComposer({ leadIds, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [delay, setDelay] = useState(3);
  const [tmpl, setTmpl] = useState(null);
  const [sending, setSending] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get("/admin/email-template").then(r => {
      setTmpl(r.data); setSubject(r.data.subject); setBody(r.data.body);
    });
  }, []);

  useEffect(() => {
    if (!job || job.status === "completed") return;
    const t = setInterval(() => {
      api.get(`/leads/bulk-email/${job.job_id || job.id}`).then(r => {
        setJob({ ...r.data, job_id: r.data.id });
        if (r.data.status === "completed") clearInterval(t);
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [job?.job_id]);

  const start = async () => {
    setSending(true);
    try {
      const r = await api.post("/leads/bulk-email", {
        lead_ids: leadIds, subject, body, delay_seconds: Number(delay),
      });
      toast.success(`Bulk send started: ${r.data.total} leads`);
      setJob(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border p-4 flex items-center justify-between">
          <div>
            <div className="overline-label text-muted-foreground">Bulk outreach</div>
            <div className="font-heading font-bold text-base mt-0.5">{leadIds.length} lead{leadIds.length !== 1 ? "s" : ""}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-accent rounded-sm"><X size={16} /></button>
        </div>

        {!job ? (
          <>
            <div className="p-4 space-y-3">
              <div>
                <div className="overline-label text-muted-foreground mb-1">Subject (placeholders allowed)</div>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="bulk-subject"
                       className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm" />
              </div>
              <div>
                <div className="overline-label text-muted-foreground mb-1">Body</div>
                <textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} data-testid="bulk-body"
                          className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm font-mono" />
              </div>
              <div>
                <div className="overline-label text-muted-foreground mb-1">Delay between sends (rate limit)</div>
                <select value={delay} onChange={(e) => setDelay(e.target.value)} data-testid="bulk-delay"
                        className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm">
                  <option value={1}>1 second</option>
                  <option value={3}>3 seconds (recommended)</option>
                  <option value={5}>5 seconds</option>
                  <option value={10}>10 seconds (safest)</option>
                  <option value={30}>30 seconds</option>
                </select>
              </div>
            </div>
            <div className="border-t border-border p-3 flex justify-end gap-2">
              <button onClick={onClose} className="border border-border px-3 py-2 text-sm rounded-sm hover:bg-accent">Cancel</button>
              <button data-testid="btn-start-bulk" disabled={sending || leadIds.length === 0} onClick={start}
                      className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50">
                <PaperPlaneTilt size={14} /> {sending ? "Starting…" : `Send to ${leadIds.length}`}
              </button>
            </div>
          </>
        ) : (
          <div className="p-6 space-y-4">
            <div className="overline-label text-muted-foreground">Job {job.status}</div>
            <div className="w-full bg-secondary h-2 rounded-sm overflow-hidden">
              <div className="bg-primary h-full transition-all" style={{ width: `${(job.progress / job.total) * 100 || 0}%` }} />
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="overline-label text-muted-foreground">Total</div><div className="font-heading font-black text-2xl">{job.total}</div></div>
              <div><div className="overline-label text-muted-foreground">Sent</div><div className="font-heading font-black text-2xl text-emerald-600">{job.sent || 0}</div></div>
              <div><div className="overline-label text-muted-foreground">Failed</div><div className="font-heading font-black text-2xl text-red-600">{job.failed || 0}</div></div>
            </div>
            {job.status === "completed" && (
              <button onClick={onClose} className="w-full bg-primary text-primary-foreground py-2 text-sm rounded-sm">Close</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
